import { reactive } from 'vue'
import { deepCopy } from '@/util/deep-copy'
import store from '@/store/index'
import moment from '@/util/MPMoment'
import { handleRefresh } from './sheet-refresh'

/*
 * 시트의 축 — 구 V2ReservationManagementDataController 에서 날짜·시간 축을 떼어냈다
 * (MDP-2295 ④-5 3단계).
 *
 * 시트가 무엇을 그릴지 정하는 두 축이다. 가로는 날짜 하루, 세로는 그 하루를 몇 분 단위로
 * 자를지(시간간격)와 잘린 칸들의 높이(timeArray)다.
 *
 * - timezone            병원 시계. 바뀌면 오늘 날짜를 다시 잡고 시트를 갱신한다
 * - selectedDate        보고 있는 날짜. 같은 날이면 다시 쓰지 않는다(불필요한 재조회 방지)
 * - selectedTimeInterval 15/30/60분. 병원별로 vuex 에 기억한다
 * - timeArray           시간 칸 목록. 위에서부터의 누적 높이를 함께 계산해 넣는다
 * - sheetStartedAt/EndedAt 그 날 운영 시간의 양 끝. 계산해 넣기만 하고 병합은 없다
 */

export type TimeRow = { time?: unknown; height: number; topMargin?: number; [extra: string]: unknown }


const currentHospitalID = (): string => store.state.hospital?.selectedHospital?._id ?? ''

export const sheetAxis = reactive({
    timezone: undefined as unknown,
    selectedDate: moment().startOf('days') as unknown,
    selectedTimeInterval:
        store.state.V2Reservation.hospitals[currentHospitalID()]?.timeInterval ?? 15,
    timeArray: [] as TimeRow[],

    sheetStartedAt: undefined as unknown,
    sheetEndedAt: undefined as unknown,
})

/**
 * 병원 시계를 바꾼다.
 *
 * 첫 로드에는 timezone 이 아직 없을 수 있다. moment.tz(undefined) 는 getter 로 동작해
 * undefined 를 돌려주고, 그러면 selectedDate 가 깨져 .format() 에서 크래시가 난다.
 * 그래서 값이 있을 때만 .tz 를 태운다.
 */
export function setTimezone(newValue: string | undefined): void {
    sheetAxis.timezone = newValue
    sheetAxis.selectedDate = newValue
        ? moment().startOf('days').tz(newValue)
        : moment().startOf('days')
    handleRefresh()
}

/** 같은 날이면 아무것도 하지 않는다 — 날짜 변경은 시트 전체 재조회를 부른다 */
export function setSelectedDate(newValue: { format(): string }): void {
    const current = sheetAxis.selectedDate as { format(): string } | undefined
    if (newValue.format() == current?.format()) {
        return
    }
    sheetAxis.selectedDate = newValue
}

/** 시간간격은 병원별로 기억한다 */
export function setSelectedTimeInterval(interval: number): void {
    store.commit('MU_V2Reservation_SetTimeInterval', {
        hospitalID: currentHospitalID(),
        timeInterval: interval,
    })
    sheetAxis.selectedTimeInterval = interval
}

/**
 * 시간 칸 목록을 갈아 끼운다.
 *
 * 받은 칸마다 위에서부터의 누적 높이(topMargin)를 계산해 넣는다 — 시트는 칸을 절대 위치로
 * 그리므로 이 값이 있어야 자리를 잡는다. 그리고 그 결과를 요일·간격별로 vuex 에 기억한다.
 */
export function setTimeArray(newValue: TimeRow[]): void {
    const temp = (deepCopy(newValue) ?? []) as TimeRow[]
    let totalTopMargin = 0
    for (let i = 0; i < temp.length; i++) {
        const t = temp[i]
        t.time = moment(t.time)
        t.topMargin = totalTopMargin
        totalTopMargin += t.height
    }
    sheetAxis.timeArray = temp
    if (sheetAxis.timeArray.length == 0) {
        return
    }

    const selectedDate = sheetAxis.selectedDate as { days(): number }
    store.commit('MU_V2ReservationCellHeight', {
        hospitalID: currentHospitalID(),
        data: {
            dayIndex: selectedDate.days(),
            interval: sheetAxis.selectedTimeInterval,
            values: deepCopy(sheetAxis.timeArray),
        },
    })
}
